// mantine
import { Button, TextInput, UnstyledButton } from '@mantine/core';
// utils
import { useFormik } from 'formik';
import { processFormData } from '@newo/toolkit/bff';
import type { IFlowDuplicateData } from '@newo/api/gateway';
import validationSchema from '@/pages/Agents/components/DuplicateFlowForm/DuplicateFlowForm.schema.ts';
import { useDuplicateFlowMutation } from '@/services/designer/flow/flow/flow.queries.ts';

interface DuplicateFlowFormProps {
    agentId: string;
    flowId: string;
    onCancel?: () => void;
    onSubmit: () => void;
}

const DuplicateFlowForm = ({
    agentId,
    flowId,
    onCancel,
    onSubmit
}: DuplicateFlowFormProps) => {
    const duplicateFlowMutation = useDuplicateFlowMutation();
    const initialValues: IFlowDuplicateData = {
        idn: '',
        title: '',
        flow_id: flowId
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            const data = processFormData(formData);
            duplicateFlowMutation.mutate(
                { agentId, data },
                {
                    onSuccess: () => {
                        onSubmit();
                        onCancel?.();
                    },
                    onSettled: () => {
                        formik.setSubmitting(false);
                    }
                }
            );
        }
    });

    const handleCancel = () => {
        onCancel?.();
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                description="Unique Identifying Name"
                error={
                    formik.touched.idn && formik.values.idn
                        ? formik.errors.idn
                        : undefined
                }
                label="Flow Idn"
                name="idn"
                placeholder="Flow Idn"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <TextInput
                withAsterisk
                error={
                    formik.touched.title && formik.values.title
                        ? formik.errors.title
                        : undefined
                }
                label="Flow Title"
                name="title"
                placeholder="Title"
                value={formik.values.title}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    Create
                </Button>
            </div>
        </form>
    );
};

export default DuplicateFlowForm;
